import { useState } from "react";
import { Code2, ChevronDown, ChevronUp } from "lucide-react";
import { PYTHON_REF } from "@/data/pythonRef";

/**
 * Collapsible Python Quick Reference panel — shows the curated methods
 * + example syntax for a given topic_id. Used at the bottom of MaterialsPage
 * for any Python chapter so students can glance & recall.
 */
export const PythonQuickRef = ({ topicId, accent }) => {
  const ref = PYTHON_REF[topicId];
  const [open, setOpen] = useState(true);

  if (!ref) return null;

  return (
    <div
      data-testid="python-quick-ref"
      className="tactile-card overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="python-quick-ref-toggle"
        className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${accent}20`, color: accent }}
          >
            <Code2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              Quick Reference
            </div>
            <div className="text-base sm:text-lg font-black truncate">
              {ref.title}
            </div>
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-5 h-5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 shrink-0 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="border-t border-border/60 max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
              <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left font-bold px-4 py-2 w-1/3">Method / Construct</th>
                <th className="text-left font-bold px-4 py-2 hidden sm:table-cell">What it does</th>
                <th className="text-left font-bold px-4 py-2 w-1/3 sm:w-1/4">Example</th>
              </tr>
            </thead>
            <tbody>
              {ref.rows.map((r, i) => (
                <tr
                  key={`${topicId}-ref-${i}`}
                  className="border-t border-border/40 hover:bg-muted/40"
                >
                  <td className="px-4 py-2.5 font-mono text-xs sm:text-sm font-semibold align-top">
                    {r.name}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground align-top hidden sm:table-cell">
                    {r.desc}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs whitespace-pre-wrap align-top">
                    {r.ex}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
